/* eslint-disable */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import { marketingApi } from '../../services/socialApi';
import { readPrerenderData, publishPrerenderData } from '../../utils/prerenderData';

/**
 * The real funnel, on the page the funnel ends at.
 *
 * Every number here comes from GET /api/social/marketing/growth. Nothing is
 * illustrative and nothing is rounded up: if a stage reads zero it is drawn as
 * zero, because a funnel diagram with invented numbers is worse than no funnel
 * diagram. As of 2026-08-19 the live shape is 29 downloads against 0 installs
 * and 0 signups, which is not a rendering bug, it is an attribution gap: the
 * download event is tracked and the two after it are not yet wired. The
 * component says so on the page rather than quietly drawing a tidy taper.
 *
 * SEEDED, because /download is prerendered. Fetching on mount without a seed
 * is precisely the mismatch that had /social/experiments, /social/achievements
 * and the rest throwing away their hydrated subtrees: the capture holds settled
 * numbers, the client's first render holds zeros, React discards the tree. The
 * seed carries what the prerenderer fetched so the first render reproduces the
 * file, and the effect refreshes from the visitor's own request one frame later.
 * See src/utils/prerenderData.js.
 */

const STAGES = [
  { key: 'click',    label: 'Clicked a tagged link' },
  { key: 'download', label: 'Downloaded' },
  { key: 'install',  label: 'Installed' },
  { key: 'signup',   label: 'Signed up' },
];

function pct(n, total) {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, (n / total) * 100));
}

export default function GrowthFunnel() {
  const seed = readPrerenderData('growthFunnel');
  const [data, setData] = useState(seed || null);

  useEffect(() => {
    let alive = true;
    marketingApi.growth()
      .then((r) => { if (alive) setData((r && r.data) || r || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // From an effect ON the value, per src/utils/prerenderData.js. Written inside
  // the promise handler it would land before the render that used it.
  useEffect(() => {
    if (data) publishPrerenderData('growthFunnel', data);
  }, [data]);

  if (!data) return null;

  const channels = Array.isArray(data.channels) ? data.channels : [];
  // Sum across channels so the picture is the whole funnel, not one code.
  const totals = STAGES.reduce((acc, s) => {
    acc[s.key] = channels.reduce((n, c) => n + (Number(c[s.key]) || 0), 0);
    return acc;
  }, {});
  const top = Math.max(...STAGES.map((s) => totals[s.key]), 0);
  const users = Number(data.total_users) || 0;
  const target = Number(data.target) || 0;
  const toTarget = target > 0 ? (users / target) * 100 : 0;

  // A stage that is zero while an EARLIER stage is not is not attrition, it is
  // an untracked event. Saying which is which is the whole value of the chart.
  const firstZeroAfterData = STAGES.findIndex((s, i) =>
    totals[s.key] === 0 && STAGES.slice(0, i).some((p) => totals[p.key] > 0));

  return (
    <Box sx={{ pt: 8 }}>
      <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>
        05 &middot; Where this is actually at
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>
        The funnel, live
      </Typography>
      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 780, mt: 1, mb: 3 }}>
        Read straight from the growth API on every load. Small numbers are shown as small numbers, because a
        public project that rounds its own metrics up is not one you should trust with a local model.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {STAGES.map((s, i) => {
              const n = totals[s.key];
              const untracked = firstZeroAfterData >= 0 && i >= firstZeroAfterData;
              return (
                <Box key={s.key} sx={{ mb: i === STAGES.length - 1 ? 0 : 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: untracked ? 'rgba(255,255,255,0.42)' : 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                      color: untracked ? 'rgba(255,255,255,0.42)' : '#fff' }}>
                      {untracked ? 'not tracked yet' : n}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct(n, top)}
                    sx={{ height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.06)',
                      '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: untracked ? 'rgba(255,255,255,0.14)' : '#7c72ff' } }}
                  />
                </Box>
              );
            })}
            {firstZeroAfterData >= 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                The greyed stages are not a drop off. Those events are not wired to attribution yet, so the
                number genuinely is not known. It would be easy to draw a plausible taper here and it would be a lie.
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(124,114,255,0.06)', border: '1px solid rgba(124,114,255,0.22)', height: '100%' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>People on the hive</Typography>
            <Typography sx={{ fontSize: '2.6rem', fontWeight: 800, lineHeight: 1.1, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
              {users}
            </Typography>
            {target > 0 && (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  {toTarget.toFixed(1)}% of the way to {target.toLocaleString()}
                </Typography>
                <LinearProgress variant="determinate" value={Math.max(0.6, Math.min(100, toTarget))}
                  sx={{ mt: 1, height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: '#17c07f' } }} />
              </>
            )}
            <Box sx={{ mt: 2.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                  {Number(data.new_users_7d) || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>new in the last 7 days</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700, color: '#fff' }}>
                  {data.top_channel || 'direct'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>busiest channel</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
